/**
 * "IT thinks this is fixed — is it?"
 *
 * The agent-proposed resolution loop from the contract: accepting closes the
 * ticket, rejecting reopens it with the reason attached. Accepting is
 * destructive-ish and irreversible from the employee's side, so it goes through
 * an `AlertDialog` whose backdrop does not dismiss; rejecting only needs a
 * reason, so it opens an inline compose box instead of a second modal.
 *
 * ## Files on the rejection, and not on the acceptance
 *
 * "It is not fixed, and here is a picture of it still not working" is the most
 * useful message this panel can carry, so the rejection box takes attachments.
 * The acceptance does not, and the asymmetry is a decision on both sides of the
 * wire rather than an omission on this one.
 *
 * Accepting closes the ticket through Ivanti's quick action and writes **no
 * journal comment at all** — there is no entry for a file to hang off, so an
 * attachment sent with `accepted: true` would have nowhere to land. The server
 * says so out loud: `TicketResolutionConfirmRequest` raises `attachment_ids are
 * only valid when accepted is false` instead of taking the ids and dropping
 * them. Refusing beats discarding, because someone told their evidence was
 * filed when it was not will keep waiting for a reply about it.
 *
 * This component honours that structurally rather than by checking a flag. The
 * two paths are mutually exclusive in the markup: while `rejecting` is true the
 * footer holding Accept is not rendered, and while it is false the `ComposeForm`
 * — and with it the uploader that owns the files — is unmounted entirely.
 * Cancelling out of a rejection therefore discards whatever was attached before
 * Accept can be reached, so there is no path on which a user attaches a file,
 * presses Accept, and silently loses it.
 *
 * The rejection gets the uploader but **no press-again prompt**. A rejection is
 * a reaction, not an answer to a formal question — the same reasoning that
 * keeps the prompt off an ordinary comment. Someone telling IT the fix did not
 * work is already out of patience, and that is the worst possible moment to put
 * an extra press in their way. The prompt stays where IT is explicitly blocked
 * and waiting: the information request.
 */

import { Alert, AlertDialog, Button, Card, Typography } from "@norican/react";
import { useState } from "react";

import { RelativeTime } from "@/components/common";
import { useLanguage, useT } from "@/i18n";

import type { TicketResolutionConfirmation } from "../api/contract";
import { useConfirmResolution } from "../api/queries";
import { useFeedbackToast } from "../lib/use-feedback-toast";
import { ComposeForm } from "./compose-form";
import { TranslatableTextView } from "./translatable-text";

export interface TicketResolutionPanelProps {
  ticketId: string;
  confirmation: TicketResolutionConfirmation;
}

export function TicketResolutionPanel({ ticketId, confirmation }: TicketResolutionPanelProps) {
  const { t } = useT("tickets");
  const { t: tCommon } = useT();
  const { language } = useLanguage();
  const toast = useFeedbackToast();
  const mutation = useConfirmResolution(ticketId);

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const handleAccept = async () => {
    try {
      // No `attachment_ids`: the field is rejected outright when `accepted` is
      // true (see the note above), and there is nothing to send anyway — the
      // uploader only exists inside the rejection form, which is not mounted
      // while the Accept button is reachable.
      await mutation.mutateAsync({
        confirmation_id: confirmation.confirmation_id,
        accepted: true,
      });
      setAcceptOpen(false);
      toast.success(t("confirmation.accepted"));
    } catch (error) {
      toast.failure(error);
    }
  };

  const handleReject = async (comment: string, attachmentIds: string[]) => {
    const detail = await mutation.mutateAsync({
      confirmation_id: confirmation.confirmation_id,
      accepted: false,
      comment,
      comment_lang: language,
      attachment_ids: attachmentIds,
    });
    setRejecting(false);
    toast.success(t("confirmation.rejected"));
    return detail;
  };

  return (
    <Card variant="outline" accent="left" accentTone="success">
      <Card.Header>
        <Typography as="h2" variant="heading-4">
          {t("confirmation.title")}
        </Typography>
        <Typography variant="body-sm" tone="muted">
          {t("confirmation.description")}
        </Typography>
      </Card.Header>

      <Card.Body>
        <div className="flex flex-col gap-3">
          <TranslatableTextView value={confirmation.resolution} multiline />

          <Typography variant="caption" tone="muted">
            {t("confirmation.proposedBy", { name: confirmation.resolved_by.display_name })}{" "}
            <RelativeTime value={confirmation.resolved_at} />
          </Typography>

          {confirmation.auto_close_at === null ? null : (
            <Alert tone="info">
              {t("confirmation.autoCloseLabel")} <RelativeTime value={confirmation.auto_close_at} />
            </Alert>
          )}

          {/* Unmounting this on cancel is what discards any attached file
              before Accept becomes reachable again — see the note at the top of
              the file. The uploader owns its files, so nothing survives it. */}
          {rejecting ? (
            <ComposeForm
              name="resolution-reject"
              label={t("confirmation.reasonLabel")}
              placeholder={t("confirmation.reasonPlaceholder")}
              submitLabel={t("confirmation.rejectSubmit")}
              autoFocus
              pending={mutation.isPending}
              error={mutation.error}
              attachments={{
                label: t("confirmation.attachLabel"),
                hint: t("confirmation.attachHint"),
              }}
              onSubmit={handleReject}
              secondaryAction={
                <Button
                  type="button"
                  variant="ghost"
                  tone="neutral"
                  onClick={() => setRejecting(false)}
                >
                  {tCommon("button.cancel")}
                </Button>
              }
            />
          ) : null}
        </div>
      </Card.Body>

      {/* Accept is unreachable while a rejection is being written, which is
          the other half of the guarantee: there is no moment at which a file is
          attached and Accept can be pressed. */}
      {rejecting ? null : (
        <Card.Footer>
          <div className="flex flex-wrap items-center gap-2">
            <Button tone="success" onClick={() => setAcceptOpen(true)}>
              {t("confirmation.accept")}
            </Button>
            <Button variant="outline" tone="neutral" onClick={() => setRejecting(true)}>
              {t("confirmation.reject")}
            </Button>
          </div>
        </Card.Footer>
      )}

      <AlertDialog.Root open={acceptOpen} onOpenChange={setAcceptOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Backdrop />
          <AlertDialog.Popup className="flex max-w-md flex-col gap-4">
            <AlertDialog.Title>{t("confirmation.acceptDialog.title")}</AlertDialog.Title>
            <AlertDialog.Description>
              {t("confirmation.acceptDialog.description")}
            </AlertDialog.Description>
            <div className="flex flex-wrap justify-end gap-2">
              <AlertDialog.Close
                render={<Button variant="ghost" tone="neutral" />}
                disabled={mutation.isPending}
              >
                {tCommon("button.cancel")}
              </AlertDialog.Close>
              <Button
                tone="success"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                onClick={() => void handleAccept()}
              >
                {t("confirmation.accept")}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </Card>
  );
}
